import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { IAPIPromptVariant, IAPIDeployment, IAPIPrompt } from 'extra/types/ApiModels'
import { TChatHistory } from 'extra/types/Messages'
import { Permission } from 'extra/types/Permissions'
import { api } from '../../api/client'
import { useEntities } from '../../context/EntitiesContext'
import { JSONHelper } from '../../../../extra/Helpers/JSONHelper'
import { ChatHistoryEditor } from '../../components/ChatHistoryEditor'
import { RequirePermission } from '../../components/RequirePermission'
import { extractErrorMessage } from '../../utils/extractErrorMessage'

interface VariantEditorProps {
  variants: Array<IAPIPromptVariant>
  deployments: Array<IAPIDeployment>
  prompt: IAPIPrompt
  onCreateVariant: () => void
  creatingVariant: boolean
}

export function VariantEditor ({ variants, deployments, prompt, onCreateVariant, creatingVariant }: VariantEditorProps) {
  const navigate = useNavigate()
  const { refreshVariants, refreshDeployments } = useEntities()

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string>('__new__')
  const [deploying, setDeploying] = useState(false)
  const [deployError, setDeployError] = useState<string | null>(null)

  const [editVariantName, setEditVariantName] = useState('')
  const [editVariantDescription, setEditVariantDescription] = useState('')
  const [editVariablesNames, setEditVariablesNames] = useState('')
  const [editInitialChatHistory, setEditInitialChatHistory] = useState<TChatHistory>([])
  const [editConfig, setEditConfig] = useState('')
  const [savingVariant, setSavingVariant] = useState(false)
  const [variantSaved, setVariantSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (variants.length > 0 && !variants.some(v => v.id === selectedVariantId)) {
      setSelectedVariantId(variants[0].id)
    }
    if (deployments.length > 0 && selectedDeploymentId === '__new__') {
      setSelectedDeploymentId(deployments[0].id)
    }
  }, [variants, deployments])

  const selectedVariant = variants.find(v => v.id === selectedVariantId) || null
  // a decision variant keeps its input template in the config, not in messages
  const isDecision = prompt.type === 'decision'

  useEffect(() => {
    if (selectedVariant) {
      setEditVariantName(selectedVariant.name)
      setEditVariantDescription(selectedVariant.description || '')
      setEditVariablesNames(JSONHelper.unescapeNewlinesDeep(JSON.stringify(selectedVariant.variablesNames)) || '[]')
      setEditInitialChatHistory(selectedVariant.initialChatHistory || [])
      setEditConfig(JSONHelper.unescapeNewlinesDeep(JSON.stringify(selectedVariant.config, null, 2)) || '{}')
      setSaveError(null)
    }
  }, [selectedVariant?.id])

  const handleSaveVariant = async (): Promise<IAPIPromptVariant | null> => {
    if (!selectedVariant || !editVariantName.trim()) return null

    setSavingVariant(true)
    setSaveError(null)

    try {
      let variablesNames = null
      try {
        variablesNames = JSON.parse(JSONHelper.escapeNewlinesInsideJsonStrings(editVariablesNames))
      } catch (e) {
        setSaveError('Invalid JSON for Variables Names')
        setSavingVariant(false)
        console.log(e)
        return null
      }

      let config = null
      try {
        config = JSON.parse(JSONHelper.escapeNewlinesInsideJsonStrings(editConfig))
      } catch (e) {
        setSaveError('Invalid JSON for Config')
        setSavingVariant(false)
        console.log(e)
        return null
      }

      const updatedVariant = await api.promptVariants.update(selectedVariant.id, {
        name: editVariantName.trim(),
        description: editVariantDescription.trim() || null,
        variablesNames,
        initialChatHistory: editInitialChatHistory,
        config,
      })
      await refreshVariants()
      setVariantSaved(true)
      setTimeout(() => setVariantSaved(false), 2000)
      return updatedVariant
    } catch (err) {
      setSaveError(await extractErrorMessage(err, 'Failed to save variant'))
      return null
    } finally {
      setSavingVariant(false)
    }
  }

  useEffect(() => {
    if (!selectedVariant) return

    const isSame =
      editVariantName === selectedVariant.name &&
      (editVariantDescription === (selectedVariant.description || '')) &&
      editVariablesNames === (JSONHelper.unescapeNewlinesDeep(JSON.stringify(selectedVariant.variablesNames)) || '[]') &&
      editConfig === (JSONHelper.unescapeNewlinesDeep(JSON.stringify(selectedVariant.config, null, 2)) || '{}') &&
      JSON.stringify(editInitialChatHistory) === JSON.stringify(selectedVariant.initialChatHistory || [])

    if (isSame) return

    setVariantSaved(false)
    const timer = setTimeout(() => {
      handleSaveVariant()
    }, 2000)

    return () => clearTimeout(timer)
  }, [
    editVariantName,
    editVariantDescription,
    editVariablesNames,
    editConfig,
    editInitialChatHistory,
    selectedVariant,
  ])

  const handleDeleteVariant = async () => {
    if (!selectedVariant) return
    if (!window.confirm(`Are you sure you want to delete the variant "${selectedVariant.name}"?`)) return

    setSavingVariant(true)
    setSaveError(null)

    try {
      await api.promptVariants.delete(selectedVariant.id)
      await refreshVariants()
      const remaining = variants.filter(v => v.id !== selectedVariant.id)
      setSelectedVariantId(remaining.length > 0 ? remaining[0].id : null)
    } catch (err) {
      setSaveError(await extractErrorMessage(err, 'Failed to delete variant'))
    } finally {
      setSavingVariant(false)
    }
  }

  const handleDeploy = async () => {
    if (!selectedVariant || !editVariantName.trim()) return

    setDeploying(true)
    setDeployError(null)

    try {
      const updatedVariant = await handleSaveVariant()
      if (!updatedVariant) {
        setDeploying(false)
        return
      }

      if (selectedDeploymentId === '__new__') {
        const autoLabel = `${prompt.name} - ${updatedVariant.name} - new deployment`
        const newDeployment = await api.deployments.create({
          variantId: updatedVariant.id,
          label: autoLabel,
        })
        await refreshDeployments()
        setSelectedDeploymentId(newDeployment.id)
        navigate(`/prompts/${prompt.id}/deployments`)
      } else {
        await api.deployments.update(selectedDeploymentId, {
          variantId: updatedVariant.id,
        })
        await refreshDeployments()
      }
    } catch (err) {
      setDeployError(await extractErrorMessage(err, 'Failed to deploy'))
    } finally {
      setDeploying(false)
    }
  }

  return (
    <div>
      {/* Variant Selection */}
      <div className="mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="variant-select" className="input-label">
              Variant
            </label>
            <select
              id="variant-select"
              value={selectedVariantId || ''}
              onChange={(e) => setSelectedVariantId(e.target.value || null)}
              className="input"
            >
              {variants.length === 0
                ? (
                <option value="">No variants</option>
                  )
                : (
                    variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name}
                  </option>
                    ))
                  )}
            </select>
          </div>
          <div className="pt-6">
            <button
              type="button"
              onClick={onCreateVariant}
              disabled={creatingVariant}
              className="btn btn-secondary"
            >
              {creatingVariant ? 'Creating...' : '+ New Variant'}
            </button>
          </div>
        </div>
      </div>

      {/* Variant Details */}
      {selectedVariant ? (
        <div className="space-y-4">
          {saveError && (
            <div className="alert-error">{saveError}</div>
          )}
          <div>
            <label htmlFor="variant-name" className="input-label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="variant-name"
              type="text"
              value={editVariantName}
              onChange={(e) => setEditVariantName(e.target.value)}
              onBlur={() => handleSaveVariant()}
              className="input"
              placeholder="Variant name"
            />
          </div>
          <div>
            <label htmlFor="variant-description" className="input-label">
              Description
            </label>
            <textarea
              id="variant-description"
              value={editVariantDescription}
              onChange={(e) => setEditVariantDescription(e.target.value)}
              onBlur={() => handleSaveVariant()}
              className="input"
              rows={1}
              placeholder="Variant description (optional)"
            />
          </div>

          {/* Variables Names */}
          <div>
            <label htmlFor="variant-variables-names" className="input-label">Variables Names</label>
            <textarea
              id="variant-variables-names"
              value={editVariablesNames}
              onChange={(e) => setEditVariablesNames(e.target.value)}
              onBlur={() => handleSaveVariant()}
              className="input font-mono text-xs"
              rows={2}
            />
          </div>

          {/* Initial Chat History */}
          {!isDecision && (
            <div className="flex flex-col gap-2">
              <label className="input-label">Initial Chat History</label>
              <ChatHistoryEditor
                history={editInitialChatHistory}
                onChange={setEditInitialChatHistory}
                onBlur={() => handleSaveVariant()}
                defaultRole="assistant"
                confirmDelete
                minHeight="min-h-[80px]"
                mode="basic"
              />
            </div>
          )}

          {/* Config */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="variant-config" className="input-label">Config</label>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-200 font-mono">
                {selectedVariant.config?._format === 'decision'
                  ? 'Decision'
                  : selectedVariant.config?._format === 'gemini-native' ? 'Gemini native' : 'OpenAI shape'}
              </span>
            </div>
            <textarea
              id="variant-config"
              value={editConfig}
              onChange={(e) => setEditConfig(e.target.value)}
              onBlur={() => handleSaveVariant()}
              className="input font-mono text-xs field-sizing-content resize-none overflow-hidden"
              rows={8}
            />
            {isDecision && (
              <p className="text-xs text-gray-400 mt-1">
                <code className="font-mono">input</code> is sent once per request and <code className="font-mono">instructions</code> once per item, both with <code className="font-mono">{'{{variable}}'}</code> placeholders. <code className="font-mono">choicesVariable</code> names the variable that holds the list of <code className="font-mono">{'{ value, description }'}</code>.
              </p>
            )}
          </div>

          {/* Save and Delete Buttons */}
          <div className="flex gap-3 items-center">
            <button
              type="button"
              onClick={handleSaveVariant}
              disabled={savingVariant || !editVariantName.trim()}
              className="btn btn-primary"
            >
              {savingVariant ? 'Saving...' : 'Save'}
            </button>
            {variantSaved && !savingVariant && <span className="text-green-400 text-xs font-medium animate-pulse">Saved</span>}

            {/* Deploy Section */}
            <RequirePermission permission={Permission.MANAGE_DEPLOYMENTS}>
              <select
                id="deploy-select"
                value={selectedDeploymentId}
                onChange={(e) => setSelectedDeploymentId(e.target.value)}
                className="input py-2"
              >
                {deployments.map((deployment) => (
                  <option key={deployment.id} value={deployment.id}>
                    {deployment.label || `Deployment ${deployment.id.slice(-8)}`}
                  </option>
                ))}
                <option value="__new__">+ New Deployment</option>
              </select>
              <button
                type="button"
                onClick={handleDeploy}
                disabled={deploying}
                className="btn btn-primary whitespace-nowrap"
              >
                {deploying ? 'Deploying...' : 'Save and Deploy'}
              </button>
            </RequirePermission>

            <div className="grow"/>
            <button
              type="button"
              onClick={handleDeleteVariant}
              disabled={savingVariant}
              className="btn btn-danger-light whitespace-nowrap"
            >
              Delete Variant
            </button>
          </div>
          {deployError && (
            <div className="alert-error">{deployError}</div>
          )}
        </div>
      ) : (
        <div className="text-gray-400">
          No variant selected. Create a new variant to get started.
        </div>
      )}
    </div>
  )
}
